import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { api } from "@/auth/AuthContext";
import { PageHeader, Card, Select, Button, Input } from "@/components/ui-kit";
import { pushSupported, subscribePush, unsubscribePush } from "@/lib/pushAlerts";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import { PaymentHistory } from "@/components/PaymentHistory";
import { SecurityCard } from "@/components/SecurityCard";
import { ReferralCard } from "@/components/ReferralCard";
import { toast } from "sonner";
import { BellRinging, BellSlash, Sun, Moon } from "@phosphor-icons/react";

const Settings = () => {
  const { t, lang, setLang } = useT();
  const { user, updatePrefs } = useAuth();
  const [riskMode, setRiskMode] = useState(user?.risk_mode || "standard");
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [propFirm, setPropFirm] = useState(user?.prop_firm_name || "");
  const [goal, setGoal] = useState(user?.trading_goal || "");
  const [theme, setTheme] = useState(localStorage.getItem("trademind_theme") || "dark");
  const [saving, setSaving] = useState(false);
  const [alertsOn, setAlertsOn] = useState(false);
  const [alertsBusy, setAlertsBusy] = useState(false);

  const applyTheme = (tm) => {
    setTheme(tm);
    localStorage.setItem("trademind_theme", tm);
    document.documentElement.classList.toggle("light", tm === "light");
  };

  useEffect(() => {
    api.get("/alerts/status").then(({ data }) => setAlertsOn(data.subscribed)).catch(() => {});
  }, []);

  const toggleAlerts = async () => {
    setAlertsBusy(true);
    try {
      if (alertsOn) {
        await unsubscribePush();
        setAlertsOn(false);
        toast.success(lang === "cs" ? "Alerty vypnuty" : "Alerts disabled");
      } else {
        await subscribePush();
        setAlertsOn(true);
        toast.success(lang === "cs" ? "Alerty zapnuty! Pošleme ti testovací notifikaci." : "Alerts enabled! Sending a test notification.");
        api.post("/alerts/test").catch(() => {});
      }
    } catch (e) {
      if (e.message === "denied") {
        toast.error(lang === "cs" ? "Notifikace jsou v prohlížeči zablokované — povol je v nastavení stránky." : "Notifications are blocked — allow them in site settings.");
      } else if (e.message === "unsupported") {
        toast.error(lang === "cs" ? "Tento prohlížeč push notifikace nepodporuje." : "This browser does not support push notifications.");
      } else {
        toast.error(lang === "cs" ? "Zapnutí alertů selhalo" : "Failed to enable alerts");
      }
    } finally {
      setAlertsBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updatePrefs({
        risk_mode: riskMode,
        language: lang,
        full_name: fullName || undefined,
        prop_firm_name: propFirm,
        trading_goal: goal,
      });
      toast.success(lang === "cs" ? "Uloženo" : "Saved");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title={t("settings.title")} sub={t("settings.sub")} />
      <div className="p-4 sm:p-6 lg:p-8 max-w-2xl space-y-4">
        <Card className="p-6 space-y-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">Email</div>
            <div className="text-base font-medium text-zinc-100 mt-1">{user?.email}</div>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{lang === "cs" ? "Jméno" : "Name"}</div>
            <div className="text-base font-medium text-zinc-100 mt-1">{user?.full_name}</div>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{t("settings.plan")}</div>
            <div className="text-base font-medium text-blue-400 mt-1">{user?.plan}</div>
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <h3 className="text-sm font-mono uppercase tracking-wider text-zinc-500">{lang === "cs" ? "Profil pro AI Mentora" : "Profile for AI Mentor"}</h3>
          <Input data-testid="settings-fullname" label={lang === "cs" ? "Jméno" : "Name"} value={fullName} onChange={(e) => setFullName(e.target.value)} />
          <Input data-testid="settings-propfirm" label={lang === "cs" ? "Aktuální prop firma" : "Current prop firm"} value={propFirm} onChange={(e) => setPropFirm(e.target.value)} placeholder="Název prop firmy" />
          <Input data-testid="settings-goal" label={lang === "cs" ? "Hlavní cíl" : "Main goal"} value={goal} onChange={(e) => setGoal(e.target.value)} placeholder={lang === "cs" ? "Chci payout do 3 měsíců" : "Payout within 3 months"} />
          <p className="text-xs text-zinc-600">{lang === "cs" ? "AI Mentor tyto údaje využije k osobnějším radám." : "AI Mentor uses this for more personal advice."}</p>
        </Card>

        <Card className="p-6 space-y-4">
          <Select
            data-testid="settings-lang"
            label={t("settings.lang")}
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            options={[{ value: "cs", label: "Čeština" }, { value: "en", label: "English" }]}
          />
          <Select
            data-testid="settings-risk"
            label={t("settings.risk")}
            value={riskMode}
            onChange={(e) => setRiskMode(e.target.value)}
            options={[
              { value: "conservative", label: t("analyze.risk.conservative") },
              { value: "standard", label: t("analyze.risk.standard") },
              { value: "aggressive", label: t("analyze.risk.aggressive") },
            ]}
          />
          <div>
            <label className="text-sm font-medium text-zinc-400 mb-1.5 block">{lang === "cs" ? "Vzhled" : "Appearance"}</label>
            <div className="flex items-center gap-2" data-testid="settings-theme-switch">
              <button
                onClick={() => applyTheme("dark")}
                data-testid="theme-dark-btn"
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm border transition-colors ${theme === "dark" ? "bg-blue-600/15 border-blue-500/40 text-blue-400" : "border-zinc-800 text-zinc-400 hover:text-zinc-100"}`}
              >
                <Moon weight="bold" size={15} /> {lang === "cs" ? "Tmavý" : "Dark"}
              </button>
              <button
                onClick={() => applyTheme("light")}
                data-testid="theme-light-btn"
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm border transition-colors ${theme === "light" ? "bg-blue-600/15 border-blue-500/40 text-blue-400" : "border-zinc-800 text-zinc-400 hover:text-zinc-100"}`}
              >
                <Sun weight="bold" size={15} /> {lang === "cs" ? "Světlý" : "Light"}
              </button>
            </div>
          </div>
          <Button onClick={save} disabled={saving} data-testid="settings-save">{saving ? "..." : t("common.save")}</Button>
        </Card>

        <Card className="p-6" data-testid="settings-alerts-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-medium text-zinc-100 flex items-center gap-2">
                <BellRinging weight="bold" size={18} className="text-blue-400" />
                {lang === "cs" ? "Chytré alerty" : "Smart alerts"}
              </h3>
              <p className="text-sm text-zinc-400 mt-1 max-w-sm">
                {lang === "cs"
                  ? "Push a e-mail upozornění při dosažení 70 % a 90 % denního limitu ztráty nebo drawdownu prop firm účtu a před důležitou zprávou z ekonomického kalendáře."
                  : "Push and email alerts at 70 % and 90 % of your prop firm daily loss limit or drawdown, and before high-impact economic events."}
              </p>
            </div>
            <button
              onClick={toggleAlerts}
              disabled={alertsBusy}
              data-testid="settings-alerts-toggle"
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50 ${alertsOn ? "bg-zinc-900 border border-zinc-700 text-zinc-300 hover:text-zinc-100" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
            >
              {alertsOn ? <><BellSlash weight="bold" size={15} /> {lang === "cs" ? "Vypnout" : "Disable"}</> : <><BellRinging weight="bold" size={15} /> {alertsBusy ? "..." : (lang === "cs" ? "Zapnout alerty" : "Enable alerts")}</>}
            </button>
          </div>
          {alertsOn && (
            <div className="text-xs font-mono text-green-400 mt-3 flex items-center gap-1.5" data-testid="settings-alerts-active">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              {lang === "cs" ? "Alerty aktivní na tomto zařízení" : "Alerts active on this device"}
            </div>
          )}
        </Card>

        <ReferralCard lang={lang} />

        <SecurityCard lang={lang} />

        {user?.plan !== "FREE" && <SubscriptionCard lang={lang} />}

        <PaymentHistory lang={lang} />

        {user?.plan === "FREE" && (
          <Card className="p-6 bg-gradient-to-br from-blue-600/10 to-zinc-950 border-blue-500/30">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-lg font-medium text-zinc-100">{lang === "cs" ? "Posuň své obchodování dál" : "Take your trading further"}</h3>
                <p className="text-sm text-zinc-400 mt-1">
                  {lang === "cs"
                    ? "PREMIUM za 499 Kč/měs odemkne 30 AI analýz denně, deník a AI mentora."
                    : "PREMIUM at 499 CZK/mo unlocks 30 AI analyses per day, the journal and AI mentor."}
                </p>
              </div>
              <Link to="/app/upgrade" data-testid="settings-upgrade" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md text-sm transition-colors whitespace-nowrap">
                {lang === "cs" ? "Upgrade na PREMIUM" : "Upgrade to PREMIUM"}
              </Link>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Settings;
